import React, { FC, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import type { DraggableAttributes } from '@dnd-kit/core'
import { useSwipeable } from 'react-swipeable'
import { PhotoProvider, PhotoView } from 'react-photo-view'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import type { Phone } from '../../../../redux/reducers/phones'
import { fetchDeleteCargoImage } from '../../../../redux/reducers/cargos'
import {
  fetchCurrentInvoices,
  setIsVisibleModalInvoice,
} from '../../../../redux/reducers/invoices'
import { fetchReportGroupedCargos } from '../../../../redux/reducers/reports'
import type { AppDispatch } from '../../../../redux/store'
import { clientsSelector } from '../../../../redux/selectors/clients-selector'
import { getRole } from '../../../../utils/getRole'
import {
  formatCargoReadinessListDate,
  parseCargoIsPaid,
  parseCargoReadinessMoment,
  parseCargoReadyForPickup,
} from './cargoReadinessDate'
import DeleteButton from '../../../buttons/actions-button/DeleteButton'
import DeleteModal from '../../DeleteModal'
import styles from '../ReportModal.module.css'
import type {
  CargoPaymentMethod,
  ReportCargoDirection,
} from '../../../../redux/types/reportGroupedCargos'
import type { PickupSenderDef, ShipmentRow } from './cargoTypes'
import { CargoPhoneBlock } from './CargoPhoneBlock'
import { buildYandexMapsHrefFromClient } from './clientMapUrl'
import { cargoCounterpartyListLabel } from './groupedCargosTransform'
import { getAmount, getItemCurrency } from '../../../../utils/getAmount'

const isPickupAddedDraftSender = (sender: PickupSenderDef) =>
  sender.rowKey.startsWith('pickup-added-')

function paymentMethodLabel(method?: CargoPaymentMethod | null): string {
  if (method === 'cash') return 'Нал'
  if (method === 'bank_transfer') return 'Безнал'
  return ''
}

function earliestReadinessAt(shipments: ShipmentRow[]): string | undefined {
  let nearestAt: string | undefined
  let nearestM: ReturnType<typeof parseCargoReadinessMoment> = null
  for (const s of shipments) {
    const at = s.sourceCargo?.readiness_at
    const m = parseCargoReadinessMoment(at)
    if (!m) continue
    if (nearestM == null || m.isBefore(nearestM)) {
      nearestM = m
      nearestAt = at ?? undefined
    }
  }
  return nearestAt
}

const ShipmentCounterpartyLine: FC<{
  row: ShipmentRow
  checklistMode: 'pickup' | 'delivered'
}> = ({ row, checklistMode }) => {
  const label = cargoCounterpartyListLabel(
    checklistMode === 'delivered' ? 'unloading' : 'loading',
    row.sourceCargo
  )
  if (!label) return null
  return (
    <span className={classNames('mb-1', styles.small3, styles.ellipsis)}>
      {label}
    </span>
  )
}

const ShipmentPaymentLine: FC<{ row: ShipmentRow }> = ({ row }) => {
  const { isDriver } = getRole()
  if (isDriver) return null

  const cargo = row.sourceCargo
  if (!cargo) return null

  const method = paymentMethodLabel(cargo.payment_method)
  const isPaid = parseCargoIsPaid(cargo.is_paid)
  const amount = Number(cargo.amount) || 0
  if (!amount && !method && !isPaid) return null

  return (
    <div className={styles.small3}>
      <b>
        {getAmount(amount)}
        {getItemCurrency(cargo.currency)}
      </b>
      {method ? (
        <>
          {' '}
          &nbsp; {method}
        </>
      ) : null}
      {isPaid ? (
        <i
          className={classNames(
            styles.icon,
            styles.iconLike,
            styles.filterGreen,
            styles.icon20,
            'ml-1 mb-1'
          )}
          title="Оплачено!"
        />
      ) : null}
    </div>
  )
}

const CargoReadyCheckIcon: FC = () => (
  <i
    className={classNames(
      styles.icon,
      styles.iconCheckGreen,
      styles.filterTurq,
      styles.cargoShipmentReadyIcon
    )}
  />
)

const SenderReadinessBadge: FC<{
  ready: boolean
  dateLabel?: string
}> = ({ ready, dateLabel }) => {
  if (!ready && !dateLabel) return null
  return (
    <span
      className={classNames(
        styles.cargoShipmentReady,
        styles.cargoSenderPickupReady
      )}
    >
      {ready ? <CargoReadyCheckIcon /> : null}
      <span className={classNames(styles.small3, styles.cargoShipmentReadyText)}>
        {ready ? (
          <b>Можно забирать</b>
        ) : (
          <span className={styles.cargoShipmentReadyDate}>{dateLabel}</span>
        )}
      </span>
    </span>
  )
}

const SenderHeaderName: FC<{
  name: string
  nameClassName?: string
}> = ({ name, nameClassName }) => (
  <b className={classNames(styles.cargoSenderHeaderMain, nameClassName)}>
    {name}
  </b>
)

const ShipmentReadyLine: FC<{
  row: ShipmentRow
  isChecked?: boolean
}> = ({ row, isChecked = false }) => {
  if (isChecked) return null
  const cargo = row.sourceCargo
  const isReady = parseCargoReadyForPickup(cargo?.can_be_picked_up)
  const dateLabel = isReady
    ? undefined
    : formatCargoReadinessListDate(cargo?.readiness_at)
  if (!isReady && !dateLabel) return null
  return (
    <span
      className={classNames(
        'd-inline-block ml-1 mr-2',
        styles.small3,
        isReady ? styles.cargoShipmentReadyText : styles.cargoShipmentReadyDate
      )}
      title={isReady ? 'Можно забирать' : dateLabel ? 'Будет готово' : undefined}
    >
      {isReady ? (
        <>
          <CargoReadyCheckIcon /> <b>Готов</b>
        </>
      ) : (
        dateLabel
      )}
    </span>
  )
}

const ShipmentTitleBlock: FC<{
  row: ShipmentRow
  checklistMode: 'pickup' | 'delivered'
}> = ({ row, checklistMode }) => (
  <>
    <div className={classNames('mb-1', styles.cargoEllipsis2)}>
      {row.title}
    </div>
    <ShipmentCounterpartyLine row={row} checklistMode={checklistMode} />
    <ShipmentPaymentLine row={row} />
  </>
)

export const stopPickupCardPointerDrag: React.PointerEventHandler = (e) => {
  e.stopPropagation()
}

/** Тот же брейкпоинт, что и свайп действий в шапке карточки */
export const PICKUP_SENDER_MOBILE_MQ = '(max-width: 575.98px)'

/** Удержание перед drag на мобильном (см. PickupSendersSortable) */
export const PICKUP_SENDER_DRAG_DELAY_MS = 500

export type PickupSenderCardCallbacks = {
  togglePickupList: (id: string) => void
  pickupChecked: Record<string, boolean>
  setSenderAndShipments: (
    mode: 'pickup' | 'delivered',
    senderKey: string,
    shipmentIds: string[],
    checked: boolean
  ) => void
  toggleShipmentOnly: (
    mode: 'pickup' | 'delivered',
    id: string,
    checked: boolean,
    parentSenderKey?: string,
    siblingShipmentIds?: string[]
  ) => void
  onRequestDeleteDelivery?: () => void
  onRequestDeleteShipment?: (row: ShipmentRow) => void
  onRequestEditShipment?: (row: ShipmentRow) => void
  onRequestAddShipment?: (sender: PickupSenderDef) => void
  canAddShipment?: boolean
  openPhoneKey: string | null
  setOpenPhoneKey: (k: string | null) => void
}

type SortableOverlay = {
  setNodeRef: (node: HTMLElement | null) => void
  style: React.CSSProperties
  attributes: DraggableAttributes
  dragHandleListeners?: Record<string, unknown>
  isDragging: boolean
}

const ShipmentPhotoGallery: FC<{
  row: ShipmentRow
  reportCargoDirection: ReportCargoDirection
}> = ({ row, reportCargoDirection }) => {
  const dispatch = useDispatch<AppDispatch>()
  const [isVisibleDeleteIconModal, setIsVisibleDeleteIconModal] =
    useState(false)
  const [imageToDelete, setImageToDelete] = useState<number | null>(null)
  const { isClient, userId } = getRole()

  const photos = row.sourceCargo?.images
  const cargoId = row.sourceCargo?.id

  if (!Array.isArray(photos) || photos.length === 0) {
    return null
  }

  return (
    <>
      <span
        className={classNames(
          'd-inline-block',
          styles.small3,
          styles.cargoShipmentActionItem
        )}
      >
        {photos.length}
      </span>
      <PhotoProvider
        key={photos.map((p) => p?.id ?? '').join('|')}
        className={classNames(styles.photoProvider, 'd-inline-block')}
        overlayRender={({ index, onClose }) => {
          const currentImage = photos[index]
          const canDelete =
            !!cargoId &&
            (!isClient ||
              (isClient &&
                currentImage?.created_by != null &&
                currentImage.created_by === userId))
          return (
            <>
              {canDelete && (
                <div
                  className={styles.btnDeleteIcon}
                  onClick={() => {
                    if (currentImage?.id) {
                      setImageToDelete(currentImage.id)
                      setIsVisibleDeleteIconModal(true)
                    }
                  }}
                />
              )}
              {isVisibleDeleteIconModal &&
                imageToDelete === currentImage?.id && (
                  <div className={styles.deleteModal}>
                    <h2 className={styles.deletePhotoTitle}>
                      Вы действительно хотите удалить фото?
                    </h2>
                    <div className={styles.deleteButtonWrapper}>
                      <button
                        type="button"
                        onClick={() => {
                          if (
                            currentImage?.id &&
                            cargoId &&
                            (!isClient ||
                              (isClient && currentImage.created_by === userId))
                          ) {
                            void dispatch(
                              fetchDeleteCargoImage({
                                imageId: currentImage.id,
                                cargoId,
                              })
                            )
                              .unwrap()
                              .then(() => {
                                setIsVisibleDeleteIconModal(false)
                                setImageToDelete(null)
                                onClose()
                                const rid = row.sourceCargo?.report_id
                                if (
                                  rid != null &&
                                  Number.isFinite(Number(rid)) &&
                                  Number(rid) > 0
                                ) {
                                  void dispatch(
                                    fetchReportGroupedCargos({
                                      reportId: Number(rid),
                                      direction: reportCargoDirection,
                                    })
                                  )
                                }
                              })
                          } else {
                            toast.error(
                              'Ошибка: не удалось определить изображение для удаления'
                            )
                            setIsVisibleDeleteIconModal(false)
                            setImageToDelete(null)
                          }
                        }}
                        className={classNames(
                          styles.btn,
                          styles.btnSmall,
                          styles.btnMarginPhotoDelete,
                          'd-block',
                          'd-sm-inline-block'
                        )}
                      >
                        Удалить
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsVisibleDeleteIconModal(false)
                          setImageToDelete(null)
                        }}
                        className={classNames(
                          styles.btn,
                          styles.btnSmall,
                          styles.btnBlueOutline,
                          'd-block d-sm-inline-block'
                        )}
                      >
                        Отмена
                      </button>
                    </div>
                  </div>
                )}
            </>
          )
        }}
      >
        {photos.map((photo, index) => (
          <PhotoView
            key={photo.id}
            src={photo?.image?.lg || photo?.image?.md || photo?.image?.sm || ''}
          >
            {index === 0 ? (
              <span
                role="button"
                tabIndex={0}
                className={classNames(
                  styles.btn,
                  styles.btnBlueOutline,
                  styles.btnSmall2,
                  styles.btnIcon,
                  styles.btnIconPhoto,
                  'd-inline-block'
                )}
                onPointerDown={stopPickupCardPointerDrag}
                aria-label="Фото груза"
              >
                <i
                  className={classNames(styles.icon, styles.cargoIconPicture)}
                />
              </span>
            ) : (
              <img style={{ display: 'none' }} alt="" />
            )}
          </PhotoView>
        ))}
      </PhotoProvider>
    </>
  )
}

function bulkSenderConfirmHeading(
  mode: 'pickup' | 'delivered',
  nextChecked: boolean
): string {
  if (mode === 'pickup') {
    return nextChecked
      ? 'Действительно пометить всё как Загружено?'
      : 'Действительно снять пометку Загружено со всех грузов?'
  }
  return nextChecked
    ? 'Действительно пометить всё как Разгружено?'
    : 'Действительно снять пометку Разгружено со всех грузов?'
}

const SenderHeaderActionCluster: FC<{
  senderRowKey: string
  dropdownKeySuffix: string
  openPhoneKey: string | null
  setOpenPhoneKey: (k: string | null) => void
  phones?: Phone[]
  mapHref: string
  hideDeliveryDelete?: boolean
  onRequestDeleteDelivery?: () => void
}> = ({
  senderRowKey,
  dropdownKeySuffix,
  openPhoneKey,
  setOpenPhoneKey,
  phones,
  mapHref,
  hideDeliveryDelete,
  onRequestDeleteDelivery,
}) => (
  <span
    className={classNames(
      'd-inline-block align-middle pl-1 pr-1',
      styles.cargoActionClusterWrap
    )}
  >
    <CargoPhoneBlock
      dropdownKey={`pickup-${senderRowKey}${dropdownKeySuffix}`}
      openKey={openPhoneKey}
      setOpenKey={setOpenPhoneKey}
      phones={phones}
    />
    <a
      target="_blank"
      rel="noopener noreferrer"
      href={mapHref}
      className={classNames(
        styles.btn,
        styles.btnBlueOutline,
        styles.btnSmall2,
        styles.btnIcon,
        styles.btnLocation,
        'align-middle'
      )}
      onPointerDown={stopPickupCardPointerDrag}
    >
      <i
        className={classNames(
          styles.icon,
          styles.cargoIconLocation,
          'd-inline-block align-middle'
        )}
      />
    </a>
    {!hideDeliveryDelete && (
      <span
        className="d-inline-block align-middle"
        onPointerDown={stopPickupCardPointerDrag}
      >
        <DeleteButton onClick={() => onRequestDeleteDelivery?.()} />
      </span>
    )}
  </span>
)

const SenderDragHandle: FC<{
  listeners?: Record<string, unknown>
}> = ({ listeners }) => {
  if (!listeners) return null
  return (
    <div
      {...listeners}
      className={classNames(
        styles.cargoDragHandle,
        styles.cargoHeaderDragInline
      )}
      aria-label="Перетащить карточку"
    >
      <span className={styles.cargoGripMark}>|||</span>
    </div>
  )
}

export type PickupSenderCardProps = PickupSenderCardCallbacks & {
  reportCargoDirection: ReportCargoDirection
  sender: PickupSenderDef
  listOpen: boolean
  phones?: Phone[]
  mapHref?: string
  checkboxDisabled?: boolean
  checklistMode?: 'pickup' | 'delivered'
  hideDeliveryDelete?: boolean
  variant: 'sortable' | 'static'
  sortable?: SortableOverlay
}

const PickupSenderCard: FC<PickupSenderCardProps> = ({
  reportCargoDirection,
  sender,
  listOpen,
  togglePickupList,
  pickupChecked,
  setSenderAndShipments,
  toggleShipmentOnly,
  onRequestDeleteDelivery,
  onRequestDeleteShipment,
  onRequestEditShipment,
  onRequestAddShipment,
  canAddShipment = true,
  openPhoneKey,
  setOpenPhoneKey,
  phones,
  mapHref,
  checkboxDisabled = false,
  checklistMode = 'pickup',
  hideDeliveryDelete = false,
  variant,
  sortable,
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const { clients } = useSelector(clientsSelector)
  const clientFromStore =
    sender.clientStoreKey &&
    !isPickupAddedDraftSender(sender) &&
    Number.isFinite(Number(sender.clientStoreKey))
      ? clients.find((c) => String(c.id) === sender.clientStoreKey)
      : undefined

  const shipmentIds = sender.shipments.map((s) => s.id)
  const senderCheckboxId = `${checklistMode}_sender_${sender.rowKey}`
  const resolvedPhones = phones ?? sender.draftPhones ?? clientFromStore?.phones
  const resolvedMapHref =
    mapHref ??
    sender.draftMapHref ??
    buildYandexMapsHrefFromClient(clientFromStore)
  const isAddedDraft = isPickupAddedDraftSender(sender)
  const checkboxDisabledResolved = checkboxDisabled
  const senderAllShipmentsChecked = !!pickupChecked[sender.rowKey]
  const isDeliveredMode = checklistMode === 'delivered'
  const hideDeliveredUi = isDeliveredMode && !!sender.hideDeliveredChecklist
  const uncheckedShipments = sender.shipments.filter((s) => !pickupChecked[s.id])
  const hasPickupReady =
    checklistMode === 'pickup' &&
    uncheckedShipments.some((s) =>
      parseCargoReadyForPickup(s.sourceCargo?.can_be_picked_up)
    )
  const senderNearestReadinessDate =
    checklistMode === 'pickup' && !hasPickupReady
      ? formatCargoReadinessListDate(
          earliestReadinessAt(uncheckedShipments)
        )
      : ''
  const showSenderReadiness = hasPickupReady || !!senderNearestReadinessDate

  const [isXsViewport, setIsXsViewport] = useState(false)
  /** xs: телефон/карта/удаление — только после свайпа; повторный свайп скрывает */
  const [mobileHeaderActionsOpen, setMobileHeaderActionsOpen] = useState(false)
  /** Визуальный сдвиг во время жеста (px), не влияет на итоговое открытие панели */
  const [mobileSwipePullX, setMobileSwipePullX] = useState(0)
  const allowMobileSwipeRef = useRef(false)
  const [bulkSenderConfirm, setBulkSenderConfirm] = useState<{
    mode: 'pickup' | 'delivered'
    nextChecked: boolean
  } | null>(null)

  useEffect(() => {
    const mq = window.matchMedia(PICKUP_SENDER_MOBILE_MQ)
    const apply = () => setIsXsViewport(mq.matches)
    apply()
    mq.addEventListener('change', apply)
    return () => mq.removeEventListener('change', apply)
  }, [])

  useEffect(() => {
    setMobileHeaderActionsOpen(false)
    setMobileSwipePullX(0)
    setBulkSenderConfirm(null)
  }, [sender.rowKey])

  const allowMobileSwipe = isXsViewport && !sender.hideHeaderContactActions
  const showMobileHeaderActions = allowMobileSwipe
  const showDesktopHeaderActions =
    !sender.hideHeaderContactActions && !showMobileHeaderActions

  allowMobileSwipeRef.current = allowMobileSwipe

  const senderHeaderActions = !sender.hideHeaderContactActions ? (
    <SenderHeaderActionCluster
      senderRowKey={sender.rowKey}
      dropdownKeySuffix=""
      openPhoneKey={openPhoneKey}
      setOpenPhoneKey={setOpenPhoneKey}
      phones={resolvedPhones}
      mapHref={resolvedMapHref}
      hideDeliveryDelete={hideDeliveryDelete}
      onRequestDeleteDelivery={onRequestDeleteDelivery}
    />
  ) : null

  const swipeHandlers = useSwipeable({
    onSwiping: (e) => {
      if (!allowMobileSwipeRef.current) return
      if (e.absX < e.absY * 0.9) {
        setMobileSwipePullX(0)
        return
      }
      const cap = 38
      const damped = e.deltaX * 0.38
      setMobileSwipePullX(Math.max(-cap, Math.min(cap, damped)))
    },
    onSwiped: (e) => {
      setMobileSwipePullX(0)
      if (!allowMobileSwipeRef.current) return
      if (e.absX < 44) return
      if (e.absX < e.absY * 1.15) return
      setMobileHeaderActionsOpen((v) => !v)
    },
    trackMouse: false,
    delta: 40,
  })

  const rootProps =
    variant === 'sortable' && sortable
      ? {
          ref: sortable.setNodeRef,
          style: sortable.style,
          ...sortable.attributes,
        }
      : {}

  return (
    <>
      <div
        {...rootProps}
        className={classNames(
          variant === 'sortable' && styles.senderItem,
          isDeliveredMode &&
            senderAllShipmentsChecked &&
            !hideDeliveredUi &&
            styles.delivered,
          styles.bxShadowSmall,
          styles.bgGray,
          styles.border10,
          styles.small2,
          checkboxDisabledResolved && styles.cargoSenderPending,
          'pt-1 pb-1 pl-2 pr-2 mb-3',
          variant === 'static' && 'sender-item client-item',
          variant === 'sortable' &&
            sortable?.isDragging &&
            styles.cargoSortableDragging,
          isAddedDraft && 'sender-item client-item'
        )}
      >
        <div
          {...(allowMobileSwipe ? swipeHandlers : {})}
          className={classNames(
            allowMobileSwipe && styles.mobileSwipeInteractiveInner
          )}
          style={
            allowMobileSwipe
              ? {
                  transform: `translateX(${mobileSwipePullX}px)`,
                  transition:
                    mobileSwipePullX !== 0 ? 'none' : 'transform 0.2s ease-out',
                }
              : undefined
          }
        >
          <div
            className={classNames(
              'pt-1 pb-1',
              styles.pickupSenderHeaderLayout
            )}
          >
            <div className={styles.pickupSenderHeaderTop}>
              <div className={styles.pickupSenderHeaderTopInner}>
                <div
                  className={classNames(
                    styles.cargoCheckboxCell,
                    senderAllShipmentsChecked &&
                      !hideDeliveredUi &&
                      styles.cargoSenderHeaderCompleted
                  )}
                >
                {hideDeliveredUi ? (
                  <div className={styles.cargoSenderHeaderNameBlock}>
                    <SenderHeaderName
                      name={sender.name}
                      nameClassName={classNames(
                        styles.cargoEllipsis2,
                        checkboxDisabledResolved && styles.cargoClientName
                      )}
                    />
                    {showSenderReadiness ? (
                      <div className={styles.pickupSenderReadyDesktop}>
                        <SenderReadinessBadge
                          ready={hasPickupReady}
                          dateLabel={senderNearestReadinessDate}
                        />
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <>
                    <input
                      type="checkbox"
                      className="green-check jsSenderAllShipments"
                      id={senderCheckboxId}
                      checked={!!pickupChecked[sender.rowKey]}
                      disabled={checkboxDisabledResolved}
                      onPointerDown={stopPickupCardPointerDrag}
                      onChange={(e) => {
                        setBulkSenderConfirm({
                          mode: checklistMode,
                          nextChecked: e.target.checked,
                        })
                      }}
                    />
                    <label
                      className={classNames(
                        checkboxDisabledResolved
                          ? undefined
                          : styles.cargoCheckboxOnly
                      )}
                      htmlFor={senderCheckboxId}
                      onPointerDown={stopPickupCardPointerDrag}
                    />
                    <div className={styles.cargoSenderHeaderNameBlock}>
                      <SenderHeaderName
                        name={sender.name}
                        nameClassName={classNames(
                          styles.cargoEllipsis2,
                          checkboxDisabledResolved && styles.cargoClientName
                        )}
                      />
                      {showSenderReadiness ? (
                        <div className={styles.pickupSenderReadyDesktop}>
                          <SenderReadinessBadge
                            ready={hasPickupReady}
                            dateLabel={senderNearestReadinessDate}
                          />
                        </div>
                      ) : null}
                    </div>
                  </>
                )}
                </div>
              </div>
              {isXsViewport && (
                <div className={styles.pickupSenderHeaderTopTrailing}>
                  {showMobileHeaderActions && (
                    <div
                      className={classNames(
                        styles.cargoHeaderActionsInner,
                        styles.cargoHeaderActionsInnerMobileSwipe
                      )}
                    >
                      <div
                        className={classNames(
                          styles.mobileActionsClusterShell,
                          mobileHeaderActionsOpen &&
                            styles.mobileActionsClusterShellOpen
                        )}
                      >
                        <div
                          className={classNames(
                            styles.mobileActionsClusterTrack,
                            mobileHeaderActionsOpen &&
                              styles.mobileActionsClusterTrackOpen
                          )}
                        >
                          <div
                            className={styles.mobileActionsClusterInnerWrap}
                          >
                            {senderHeaderActions}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {variant === 'sortable' && (
                    <SenderDragHandle
                      listeners={sortable?.dragHandleListeners}
                    />
                  )}
                </div>
              )}
            </div>

            <div
              className={classNames(
                styles.pickupSenderHeaderMeta,
                hideDeliveredUi && styles.pickupSenderHeaderMetaFlush
              )}
            >
              {showSenderReadiness ? (
                <div className={styles.pickupSenderReadyMobile}>
                  <SenderReadinessBadge
                    ready={hasPickupReady}
                    dateLabel={senderNearestReadinessDate}
                  />
                </div>
              ) : null}
              <div className={styles.pickupSenderHeaderControls}>
                <div className={styles.cargoCountAddRow}>
                  <div
                    role="button"
                    tabIndex={0}
                    className={classNames(
                      styles.small2,
                      'd-inline-block small-2 pointer',
                      styles.cargoPointer,
                      styles.cargoListToggleHit,
                      { [styles.cargoListToggleOpen]: listOpen }
                    )}
                    onPointerDown={stopPickupCardPointerDrag}
                    onClick={() =>
                      sender.listId && togglePickupList(sender.listId)
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ')
                        sender.listId && togglePickupList(sender.listId)
                    }}
                  >
                    <i
                      className={classNames(
                        styles.icon,
                        styles.cargoIconBox,
                        styles.cargoIcon20,
                        styles.cargoIconBlue,
                        'mr-0'
                      )}
                    />{' '}
                    <span>{sender.count ?? 0}</span>{' '}
                    <i
                      className={classNames(
                        styles.icon,
                        styles.cargoIconArrow,
                        styles.cargoIcon10,
                        'ml-0 mr-0'
                      )}
                    />
                  </div>
                  {canAddShipment && onRequestAddShipment && (
                    <div
                      role="button"
                      tabIndex={0}
                      className={classNames(
                        styles.btn,
                        styles.btnBlueOutline,
                        styles.btnSmall2,
                        styles.addCargoButton,
                        'ml-1'
                      )}
                      onPointerDown={stopPickupCardPointerDrag}
                      onClick={() => onRequestAddShipment(sender)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          onRequestAddShipment(sender)
                        }
                      }}
                    >
                      {'\uFF0B'}
                    </div>
                  )}
                </div>

                {showDesktopHeaderActions && (
                  <div className={styles.cargoHeaderActionsInner}>
                    {senderHeaderActions}
                    {variant === 'sortable' && (
                      <SenderDragHandle
                        listeners={sortable?.dragHandleListeners}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div
            className={classNames(
              'pl-5 ml-1',
              styles.cargoShipmentPickupItems,
              listOpen
                ? styles.cargoShipmentPickupItemsOpen
                : styles.cargoShipmentPickupItemsClosed
            )}
            onPointerDown={stopPickupCardPointerDrag}
          >
            {sender.shipments.map((row) => {
              const shipmentCheckboxId = `${checklistMode}_shipment_${row.id}`
              const readyTitle =
                checklistMode === 'pickup' ? 'Загружено' : 'Разгружено'
              return (
                <div
                  key={row.id}
                  className={classNames(
                    styles.cargoPickupItem,
                    isDeliveredMode &&
                      pickupChecked[row.id] &&
                      !hideDeliveredUi &&
                      styles.cargoPickupItemDelivered,
                    styles.cargoBgWhite,
                    styles.border10,
                    styles.cargoBxShadow,
                    styles.small2,
                    'pickup-item mb-1 pt-1 pb-1'
                  )}
                >
                  <div className="row align-items-center ml-1 mr-1">
                    <div className="col-12 col-sm pl-1">
                      {hideDeliveredUi ? (
                        <ShipmentTitleBlock
                          row={row}
                          checklistMode={checklistMode}
                        />
                      ) : (
                        <>
                          <input
                            type="checkbox"
                            className="green-check"
                            id={shipmentCheckboxId}
                            checked={!!pickupChecked[row.id]}
                            disabled={checkboxDisabledResolved}
                            onChange={(e) =>
                              toggleShipmentOnly(
                                checklistMode,
                                row.id,
                                e.target.checked,
                                sender.rowKey,
                                shipmentIds
                              )
                            }
                          />
                          <label
                            className="hint"
                            title={readyTitle}
                            htmlFor={shipmentCheckboxId}
                            onPointerDown={stopPickupCardPointerDrag}
                          >
                            <ShipmentTitleBlock
                              row={row}
                              checklistMode={checklistMode}
                            />
                          </label>
                        </>
                      )}
                    </div>
                    <div
                      className="col-12 col-sm-auto pl-1 pr-0"
                      style={{ textAlign: 'right' }}
                    >
                      {row.sourceCargo?.invoice_id ? (
                        <div
                          className={styles.shipmentInvoiceLink}
                          onPointerDown={stopPickupCardPointerDrag}
                          onClick={() => {
                            const invoiceId = row.sourceCargo?.invoice_id
                            if (!invoiceId) return
                            void dispatch(fetchCurrentInvoices(invoiceId))
                              .unwrap()
                              .then(() => {
                                dispatch(setIsVisibleModalInvoice(true))
                              })
                              .catch(() => {
                                toast.error('Не удалось открыть счёт')
                              })
                          }}
                          role="button"
                          tabIndex={0}
                          title={`Счёт №${row.sourceCargo.invoice_id}`}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              const invoiceId = row.sourceCargo?.invoice_id
                              if (!invoiceId) return
                              void dispatch(fetchCurrentInvoices(invoiceId))
                                .unwrap()
                                .then(() => {
                                  dispatch(setIsVisibleModalInvoice(true))
                                })
                                .catch(() => {
                                  toast.error('Не удалось открыть счёт')
                                })
                            }
                          }}
                        >
                          <i
                            className={classNames(
                              styles.icon,
                              styles.icon15,
                              styles.iconInvoice
                            )}
                          />
                          № {row.sourceCargo.invoice_id}
                        </div>
                      ) : null}
                      {checklistMode === 'pickup' ? (
                        <ShipmentReadyLine
                          row={row}
                          isChecked={!!pickupChecked[row.id]}
                        />
                      ) : null}
                      <ShipmentPhotoGallery
                        row={row}
                        reportCargoDirection={reportCargoDirection}
                      />
                      {onRequestEditShipment && (
                        <div
                          className={classNames(
                            styles.btn,
                            styles.btnBlueOutline,
                            styles.btnSmall2,
                            styles.btnIcon,
                            'd-inline-block'
                          )}
                          onPointerDown={stopPickupCardPointerDrag}
                          onClick={() => onRequestEditShipment(row)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              onRequestEditShipment(row)
                            }
                          }}
                        >
                          <i
                            className={classNames(styles.icon, styles.iconEdit)}
                          />
                        </div>
                      )}
                      {row.showDelete && (
                        <div
                          className={classNames(
                            styles.btn,
                            styles.btnSmall2,
                            styles.btnIcon,
                            styles.btnOutline,
                            'd-inline-block'
                          )}
                          onPointerDown={stopPickupCardPointerDrag}
                          onClick={() => onRequestDeleteShipment?.(row)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              onRequestDeleteShipment?.(row)
                            }
                          }}
                        >
                          <i
                            className={classNames(
                              styles.icon,
                              styles.iconDeleteRed
                            )}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
      {bulkSenderConfirm && (
        <DeleteModal
          skipDispatch
          variant="confirm"
          heading={bulkSenderConfirmHeading(
            bulkSenderConfirm.mode,
            bulkSenderConfirm.nextChecked
          )}
          onConfirm={() => {
            const p = bulkSenderConfirm
            if (!p) return
            setBulkSenderConfirm(null)
            void setSenderAndShipments(
              p.mode,
              sender.rowKey,
              shipmentIds,
              p.nextChecked
            )
          }}
          onCancel={() => setBulkSenderConfirm(null)}
        />
      )}
    </>
  )
}

export default PickupSenderCard
